import { useRef, useState } from 'react'

import { useV1ClassesList, useV1ClassesStudentsTransferPreviewCreate } from '@/api/client'
import type { ClassList, StudentRosterRow } from '@/api/model'
import { LookupTruncationNote } from '@/components/lookup-truncation-note'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { JOURNAL_NUMBER_MAX, journalNumberError, transferTargetQuery } from './student-transfer'
import { studentLogin } from '@/lib/student-login'

/**
 * TR-605 transfer dialog: move an Active student to a different Active class. The operator picks
 * a target class and a journal number for it — the dialog fetches the backend preview for that
 * class to pre-fill the proposed next-free number and to validate the operator's choice against
 * the numbers already taken there (a mid-range hole is allowed, a taken number is refused inline).
 * The caller owns the mutation; this dialog is dumb and controlled (like the 011 lifecycle dialogs).
 *
 * Target classes are every active class available to the operator, minus the source class. This
 * deliberately includes other schools: TR-1002 R3 requires an administrative school transfer to
 * update relations without deriving or changing the immutable login.
 */
export function TransferStudentDialog({
  open,
  onOpenChange,
  row,
  sourceClassId,
  sourceClassName,
  sourceIsOpenProgram,
  pending,
  error,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  row: StudentRosterRow | null
  sourceClassId: number
  sourceClassName: string
  sourceIsOpenProgram: boolean
  pending?: boolean
  error?: string | null
  onConfirm: (targetClassId: number, journalNumber: number) => void
}) {
  const [targetClassId, setTargetClassId] = useState<number | null>(null)
  const [targetSearch, setTargetSearch] = useState('')
  const [journalInput, setJournalInput] = useState('')
  const [occupied, setOccupied] = useState<number[]>([])
  const [proposed, setProposed] = useState<number | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  // Monotonic token to discard out-of-order preview responses: only the latest request's result
  // may write state, so a slow response for a previously-selected class can't clobber the current
  // class's proposal/occupied set (which would defeat the client-side taken-number check).
  const previewSeq = useRef(0)

  // Reset ALL local state whenever the dialog opens for a new row — done during render (the
  // documented "adjust state on prop change" pattern, as in restore-student-dialog), so there
  // is no effect round-trip and no prior row's preview leaks into a freshly opened dialog.
  const [wasOpen, setWasOpen] = useState(false)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setTargetClassId(null)
      setTargetSearch('')
      setJournalInput('')
      setOccupied([])
      setProposed(null)
      setPreviewing(false)
      setPreviewError(null)
    }
  }

  // Eligible targets: all active classes visible to the operator, including other schools.
  const classesQuery = useV1ClassesList(transferTargetQuery(targetSearch, sourceIsOpenProgram), {
    query: { enabled: open },
  })
  const allClasses: ClassList[] =
    classesQuery.data?.status === 200 ? classesQuery.data.data.results : []
  const targets = allClasses.filter((c) => c.id !== sourceClassId)
  const sourceIncludedInCount =
    targetSearch.trim() === '' || allClasses.some((c) => c.id === sourceClassId)
  const targetsCount =
    classesQuery.data?.status === 200
      ? Math.max(0, classesQuery.data.data.count - (sourceIncludedInCount ? 1 : 0))
      : targets.length
  const targetClass = targets.find((c) => c.id === targetClassId) ?? null

  // Preview for the chosen target: proposed number + occupied set. Fired only once a target is
  // picked; while it loads the number field and the class select are disabled and confirm is blocked.
  const previewQuery = useV1ClassesStudentsTransferPreviewCreate()

  const loadPreview = async (classId: number) => {
    if (!row) return
    const seq = ++previewSeq.current
    setPreviewing(true)
    setPreviewError(null)
    setProposed(null)
    setOccupied([])
    const response = await previewQuery.mutateAsync({
      id: sourceClassId,
      membershipPk: String(row.id),
      data: { target_class_id: classId },
    })
    // Ignore a stale response: a newer target was picked while this one was in flight.
    if (seq !== previewSeq.current) return
    setPreviewing(false)
    if (response.status === 200) {
      setProposed(response.data.proposed_journal_number)
      setOccupied(response.data.occupied_journal_numbers)
      setJournalInput(String(response.data.proposed_journal_number))
      return
    }
    setPreviewError('Nie udało się pobrać propozycji numeru dla wybranej klasy.')
  }

  const onTargetChange = (value: string) => {
    const id = Number(value)
    setTargetClassId(id)
    void loadPreview(id)
  }

  const journalNumber = journalInput === '' ? undefined : Number(journalInput)
  const numberError =
    targetClassId === null || previewing || proposed === null
      ? null
      : journalNumberError(journalNumber, occupied)
  const canConfirm =
    !pending &&
    !previewing &&
    targetClassId !== null &&
    proposed !== null &&
    journalNumber !== undefined &&
    numberError === null

  const login = row ? studentLogin(row) : ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Przeniesienie ucznia do innej klasy</DialogTitle>
          <DialogDescription>
            Login <span className="font-mono font-medium">{login}</span> pozostaje bez zmian. Zmieni
            się przypisanie ucznia do klasy oraz jego bieżący numer w dzienniku.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="transfer-target-search">Wyszukaj klasę docelową</Label>
            <Input
              id="transfer-target-search"
              type="search"
              value={targetSearch}
              disabled={previewing}
              placeholder="Nazwa klasy lub szkoły"
              onChange={(event) => {
                setTargetSearch(event.target.value)
                setTargetClassId(null)
                setJournalInput('')
                setOccupied([])
                setProposed(null)
                setPreviewError(null)
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="transfer-target-class">Klasa docelowa</Label>
            <Select
              value={targetClassId ? String(targetClassId) : ''}
              onValueChange={onTargetChange}
              disabled={previewing || classesQuery.isFetching}
            >
              <SelectTrigger id="transfer-target-class" className="w-full">
                <SelectValue placeholder="Wybierz klasę docelową" />
              </SelectTrigger>
              <SelectContent>
                {targets.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name} — {c.school_name}
                  </SelectItem>
                ))}
                <LookupTruncationNote count={targetsCount} shown={targets.length} />
              </SelectContent>
            </Select>
            {targets.length === 0 && classesQuery.data?.status === 200 && (
              <p className="text-sm text-muted-foreground">
                Brak innej aktywnej klasy, do której można przenieść ucznia.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="transfer-journal-number">Numer w dzienniku (klasa docelowa)</Label>
            <Input
              id="transfer-journal-number"
              type="number"
              min={1}
              max={JOURNAL_NUMBER_MAX}
              value={journalInput}
              disabled={targetClassId === null || previewing}
              onChange={(e) => setJournalInput(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {previewing
                ? 'Pobieranie propozycji numeru…'
                : proposed !== null
                  ? `Domyślnie proponowany kolejny wolny numer: ${proposed}. Możesz wpisać dowolny wolny numer, także „w środku" zakresu.`
                  : 'Wybierz klasę docelową, aby zobaczyć proponowany numer.'}
            </p>
            {numberError && <p className="text-sm text-destructive">{numberError}</p>}
            {previewError && <p className="text-sm text-destructive">{previewError}</p>}
          </div>

          {targetClass && journalNumber !== undefined && numberError === null && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="mb-1 font-medium">Podsumowanie</p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <dt className="text-muted-foreground">Login</dt>
                <dd className="font-mono">{login}</dd>
                <dt className="text-muted-foreground">Klasa źródłowa</dt>
                <dd>{sourceClassName}</dd>
                <dt className="text-muted-foreground">Klasa docelowa</dt>
                <dd>
                  {targetClass.name} — {targetClass.school_name}
                </dd>
                <dt className="text-muted-foreground">Nowy numer w dzienniku</dt>
                <dd>{journalNumber}</dd>
              </dl>
              <p className="mt-2 text-xs text-muted-foreground">
                Login pozostanie taki sam po zatwierdzeniu przeniesienia.
              </p>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Anuluj
          </Button>
          <Button
            type="button"
            onClick={() => {
              if (targetClassId !== null && journalNumber !== undefined) {
                onConfirm(targetClassId, journalNumber)
              }
            }}
            disabled={!canConfirm}
          >
            Przenieś
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
